import React, { useState } from 'react';
import { Card, Button } from '../components/UI';
import { FilePdf, Spinner as SpinnerIcon, CheckCircle, Download } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useBranding } from '../context/BrandingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FEATURE_SECTIONS = [
  {
    title: 'Planning Module',
    color: [0, 47, 167],
    features: [
      { name: 'Dashboard', desc: 'Real-time overview of all projects with KPIs: total projects, active projects, teams, pending tasks, upcoming milestones, task completion analytics, and recent project list.' },
      { name: 'Project Management', desc: 'Full lifecycle project management. Create projects with client details, location, dates, contract values, and status tracking. Supports multiple project types and statuses.' },
      { name: 'Team Management', desc: 'Organize teams by trade/category. Assign leaders, add members, and link teams to projects. Supports civil, mechanical, electrical, and custom categories.' },
      { name: 'Work Breakdown Structure (WBS)', desc: 'Hierarchical work organization: Trade > Subcategory > Activity. Link WBS activities to projects for structured budgeting and cost tracking.' },
      { name: 'Milestones & Tasks', desc: 'Project milestones with planned dates. Tasks with priority levels, assignees, due dates, status tracking, and subtask breakdown for detailed planning.' },
    ]
  },
  {
    title: 'Execution Module',
    color: [0, 150, 80],
    features: [
      { name: 'Daily Site Logs', desc: 'Record daily progress per team with project, activity, progress percentage, and remarks. Logs feed into reports and are locked after team report submission.' },
      { name: 'Manpower Tracking', desc: 'Log worker counts and hours by trade category. Auto-calculates man-hours. Summary views by date, project, and category.' },
      { name: 'Material Tracking', desc: 'Track materials received, used, and transferred. Auto-calculates running balance. Aggregated summaries and integration with inventory module.' },
      { name: 'Team Reports', desc: 'Consolidate daily logs, manpower, and materials into team reports. Submission locks underlying data for audit integrity.' },
      { name: 'Productivity Analysis', desc: 'Analyze labor productivity with planned vs actual comparisons. Efficiency metrics per activity and team to identify improvement areas.' },
    ]
  },
  {
    title: 'Finance Module',
    color: [180, 100, 0],
    features: [
      { name: 'Budget Management', desc: 'Set material, labor, and overhead budgets per project and WBS activity. Define unit costs and quantities for detailed budget planning.' },
      { name: 'Cost Tracking', desc: 'Comprehensive cost tracking with auto-derivation from execution data. Manual expense entry. All costs generate double-entry journal entries.' },
      { name: 'Budget vs Actual Analysis', desc: 'Variance analysis comparing budgeted vs actual costs. Percentage overrun/underrun calculations with drill-down by activity.' },
      { name: 'Invoice Management', desc: 'Create invoices with line items, VAT/tax calculations. Track status (Draft, Sent, Partially Paid, Paid). Auto-generates accounting entries.' },
      { name: 'Payment Processing', desc: 'Record payments against invoices with amount, date, method, and reference. Auto-updates invoice status and creates accounting entries.' },
      { name: 'Variations (Change Orders)', desc: 'Manage change orders through approval workflow. Approved variations update contract values and create journal entries.' },
      { name: 'Financial Dashboard', desc: 'Consolidated financial view: revenue, costs, profit margins, cash flow, outstanding receivables, and project-level financial health.' },
    ]
  },
  {
    title: 'Payroll & HR',
    color: [128, 0, 128],
    features: [
      { name: 'Employee Management', desc: 'Complete employee profiles with personal details, employment info, salary structures, and contract terms.' },
      { name: 'Salary Structures', desc: 'Configurable salary components: Basic, Housing Allowance, Transport Allowance, Other Allowances. Support for overtime and deduction calculations.' },
      { name: 'Payroll Processing', desc: 'Monthly payroll runs with auto-calculation of gross pay, deductions, overtime, and net pay. Generate individual payslips.' },
      { name: 'End of Service (EOS)', desc: 'Calculate End of Service benefits per UAE labor law. Auto-computation based on service duration and last salary.' },
      { name: 'Payroll Accounting', desc: 'Auto-generated journal entries for payroll (debit salary expense, credit payables). Seamless integration with the accounting module.' },
    ]
  },
  {
    title: 'Inventory Management',
    color: [0, 128, 128],
    features: [
      { name: 'Warehouse Management', desc: 'Set up multiple warehouses with location details. Track stock levels per warehouse. Support for main stores and project-specific stores.' },
      { name: 'Supplier Database', desc: 'Maintain supplier profiles with contact details, payment terms, and category. Link suppliers to purchase orders.' },
      { name: 'Purchase Orders', desc: 'Create POs with line items, quantities, and prices. Track PO status through approval and delivery. Integration with supplier database.' },
      { name: 'Stock Movements', desc: 'Record stock in (receiving), stock out (issuing), and transfers between warehouses. Auto-updates stock levels and costs.' },
    ]
  },
  {
    title: 'Accounting & Ledgers',
    color: [100, 50, 0],
    features: [
      { name: 'Chart of Accounts', desc: 'Configurable chart of accounts with account types (Asset, Liability, Equity, Revenue, Expense). Hierarchical account structure.' },
      { name: 'General Ledger', desc: 'Complete transaction history per account. Filter by date range and account. Debit and credit entries with running balance.' },
      { name: 'Journal Entries', desc: 'All financial transactions create balanced double-entry journal entries. Manual entry supported for adjustments.' },
      { name: 'Trial Balance', desc: 'Real-time trial balance verifying debit = credit integrity. Filter by date range for period-specific balances.' },
      { name: 'Profit & Loss Statement', desc: 'Income statement showing revenue, cost of goods, gross profit, operating expenses, and net profit for any period.' },
      { name: 'Balance Sheet', desc: 'Statement of financial position showing assets, liabilities, and equity at any point in time.' },
    ]
  },
  {
    title: 'Reports & Analytics',
    color: [0, 100, 180],
    features: [
      { name: 'Weekly Reports', desc: 'Auto-generated reports: Technical (milestones, tasks), Operational (manpower, materials), Financial (costs, revenue, P&L). PDF and Excel export.' },
      { name: 'Executive Dashboard', desc: 'High-level KPIs across all projects. Portfolio health, financial summaries, resource allocation, and risk indicators.' },
      { name: 'Project Dashboard', desc: 'Project-specific performance dashboard with progress, financial status, team performance, and risk assessment.' },
      { name: 'Advanced Reports', desc: 'Project Profitability, Resource Utilization, Payroll Summary, and Inventory Valuation reports with export capabilities.' },
    ]
  },
  {
    title: 'Administration & Security',
    color: [50, 50, 50],
    features: [
      { name: 'Role-Based Access Control', desc: '11 configurable roles with editable permission matrix. Admin can customize module access per role. UAE construction industry defaults provided.' },
      { name: 'User Invitation System', desc: 'Streamlined onboarding via shareable invite links. Admin creates invite with email and role. User sets own password. Auto-login on acceptance.' },
      { name: 'Multi-Tenant Architecture', desc: 'Complete data isolation per client organization. Each tenant has independent users, projects, and settings. Super Admin manages all tenants.' },
      { name: 'Subscription Management', desc: 'Per-tenant subscription tracking with start/end dates, plan types, and notes. Super Admin can suspend/reactivate tenants based on payment status.' },
      { name: 'Tax/VAT Compliance', desc: 'Configurable VAT/GST rates, tax registration, and VAT report generation for UAE compliance.' },
      { name: 'Backup & Restore', desc: 'Manual backup creation with restore capability. Backup size tracking and history. Super Admin managed.' },
      { name: 'Audit Trail', desc: 'Complete audit log of all system actions. User logins, data changes, permission updates, and admin actions tracked.' },
      { name: 'Dark/Light Theme', desc: 'Professional theme engine with Light, Dark, and System (auto-detect) modes. All UI components adapt seamlessly.' },
      { name: 'Legal Compliance', desc: 'Built-in Terms of Use, Privacy Policy, and Disclaimer management. User acceptance tracking and legal admin panel.' },
    ]
  },
];

export default function MarketingManualPage() {
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const branding = useBranding();

  const generatePDF = () => {
    setGenerating(true);
    setDone(false);

    setTimeout(() => {
      try {
        const doc = new jsPDF();
        const pw = doc.internal.pageSize.getWidth();
        const ph = doc.internal.pageSize.getHeight();

        // ===== COVER PAGE =====
        doc.setFillColor(0, 47, 167);
        doc.rect(0, 0, pw, ph, 'F');
        // White accent bar
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 60, pw, 2, 'F');
        doc.rect(0, 200, pw, 2, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(42);
        doc.setFont(undefined, 'bold');
        doc.text('ProjectBook Pro\u2122', pw / 2, 90, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'normal');
        doc.text('Project ERP & Control System', pw / 2, 105, { align: 'center' });

        doc.setFontSize(11);
        doc.text('PLAN  \u2022  BUILD  \u2022  DELIVER', pw / 2, 125, { align: 'center' });

        doc.setFontSize(22);
        doc.setFont(undefined, 'bold');
        doc.text('Product & Training Manual', pw / 2, 160, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('Comprehensive Feature Guide for Construction ERP', pw / 2, 175, { align: 'center' });

        // Bottom branding block
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('SGK Apps', pw / 2, 220, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('A product of VRV Technical & Technology Solutions FZ LLC', pw / 2, 230, { align: 'center' });
        doc.text('info@sgkapps.com  |  www.projectbookpro.com', pw / 2, 240, { align: 'center' });
        doc.text(`Version 1.0  |  ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pw / 2, 255, { align: 'center' });

        // ===== TABLE OF CONTENTS =====
        doc.addPage();
        doc.setFillColor(0, 47, 167);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('Table of Contents', 14, 24);

        let y = 50;
        doc.setTextColor(0);
        let sectionNum = 1;
        FEATURE_SECTIONS.forEach(sec => {
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(sec.color[0], sec.color[1], sec.color[2]);
          doc.text(`${sectionNum}. ${sec.title}`, 14, y);
          y += 6;
          doc.setFontSize(9);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(100, 100, 100);
          sec.features.forEach(f => {
            doc.text(`    \u2022 ${f.name}`, 20, y);
            y += 4.5;
          });
          y += 4;
          sectionNum++;
          if (y > 270) { doc.addPage(); y = 20; }
        });
        // Add FAQ and Support in TOC
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 47, 167);
        doc.text(`${sectionNum}. Frequently Asked Questions`, 14, y); y += 6;
        doc.text(`${sectionNum + 1}. Support & Contact`, 14, y);

        // ===== FEATURE SECTIONS =====
        sectionNum = 1;
        FEATURE_SECTIONS.forEach(sec => {
          doc.addPage();
          // Section header
          doc.setFillColor(sec.color[0], sec.color[1], sec.color[2]);
          doc.rect(0, 0, pw, 35, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.text(`${sectionNum}. ${sec.title}`, 14, 24);

          y = 48;
          sec.features.forEach((f, idx) => {
            if (y > 255) { doc.addPage(); y = 20; }

            // Feature name
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(sec.color[0], sec.color[1], sec.color[2]);
            doc.text(`${sectionNum}.${idx + 1}  ${f.name}`, 14, y);
            y += 7;

            // Feature description
            doc.setFontSize(9.5);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(50, 50, 50);
            const lines = doc.splitTextToSize(f.desc, 180);
            doc.text(lines, 14, y);
            y += lines.length * 4.5 + 4;

            // Subtle separator
            if (idx < sec.features.length - 1) {
              doc.setDrawColor(220, 220, 220);
              doc.line(14, y, pw - 14, y);
              y += 6;
            }
          });
          sectionNum++;
        });

        // ===== FAQ SECTION =====
        doc.addPage();
        doc.setFillColor(0, 47, 167);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(`${sectionNum}. Frequently Asked Questions`, 14, 24);

        y = 48;
        const faqs = [
          { q: 'What is ProjectBook Pro\u2122?', a: 'ProjectBook Pro\u2122 is a comprehensive Project ERP & Control System designed for the construction industry. It covers Planning, Execution, Finance, Payroll, Inventory, Accounting, and Reporting in a single integrated platform.' },
          { q: 'How is ProjectBook Pro\u2122 licensed?', a: 'ProjectBook Pro\u2122 is licensed on an annual subscription basis per organization. Each subscription includes all modules, unlimited projects, and multi-user access.' },
          { q: 'Is my data secure?', a: 'Yes. ProjectBook Pro\u2122 uses multi-tenant architecture with complete data isolation. Each client organization has its own isolated database space. All connections are encrypted.' },
          { q: 'How many users can I have?', a: 'There is no limit on the number of users per tenant. You can create unlimited users with different roles and permission levels.' },
          { q: 'Can I customize role permissions?', a: 'Yes. ProjectBook Pro\u2122 includes 11 pre-configured roles with an editable permission matrix. Admins can customize which modules each role can access.' },
          { q: 'Does it support VAT/GST?', a: 'Yes. ProjectBook Pro\u2122 includes configurable tax rates, automatic tax calculations on invoices, and VAT report generation for compliance filing.' },
          { q: 'Can I export data?', a: 'Yes. Reports can be exported to PDF and Excel. Financial statements, payslips, and invoices all have export capabilities.' },
          { q: 'Is training provided?', a: 'Yes. This manual serves as the training guide. Additionally, the built-in User Guide within the application provides module-by-module documentation and FAQ.' },
          { q: 'What if my subscription expires?', a: 'The Super Admin can suspend or reactivate your account. Suspended accounts display a "Service Paused" message. Data is preserved and restored upon reactivation.' },
          { q: 'How do I get support?', a: 'Contact SGK Apps at info@sgkapps.com for technical support, training requests, or subscription inquiries.' },
        ];

        faqs.forEach((f, i) => {
          if (y > 255) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 47, 167);
          doc.text(`Q${i + 1}: ${f.q}`, 14, y); y += 5.5;
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(9.5);
          const aLines = doc.splitTextToSize(f.a, 180);
          doc.text(aLines, 14, y); y += aLines.length * 4.5 + 7;
        });

        // ===== SUPPORT & CONTACT =====
        doc.addPage();
        doc.setFillColor(0, 47, 167);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(`${sectionNum + 1}. Support & Contact`, 14, 24);

        y = 55;
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('SGK Apps', 14, y); y += 8;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text('A product of VRV Technical & Technology Solutions FZ LLC', 14, y); y += 14;

        const contacts = [
          ['Email', 'info@sgkapps.com'],
          ['Website', 'www.projectbookpro.com'],
          ['Product', 'ProjectBook Pro\u2122 - Project ERP & Control System'],
          ['Version', '1.0'],
        ];
        contacts.forEach(([label, val]) => {
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 47, 167);
          doc.text(`${label}:`, 14, y);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(50, 50, 50);
          doc.text(val, 50, y);
          y += 7;
        });

        y += 15;
        doc.setFillColor(245, 248, 255);
        doc.roundedRect(14, y, pw - 28, 50, 3, 3, 'F');
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 47, 167);
        doc.text('Thank you for choosing ProjectBook Pro\u2122', pw / 2, y + 15, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text('For training, support, or partnership inquiries:', pw / 2, y + 28, { align: 'center' });
        doc.setFont(undefined, 'bold');
        doc.text('info@sgkapps.com', pw / 2, y + 38, { align: 'center' });

        // ===== ADD FOOTER BRANDING =====
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 2; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setDrawColor(200, 200, 200);
          doc.line(14, ph - 18, pw - 14, ph - 18);
          doc.setFontSize(7);
          doc.setTextColor(160, 160, 160);
          doc.text('ProjectBook Pro\u2122 by SGK Apps  |  info@sgkapps.com  |  VRV Technical & Technology Solutions FZ LLC', 14, ph - 12);
          doc.text(`Page ${i - 1} of ${pageCount - 1}`, pw - 14, ph - 12, { align: 'right' });
        }

        doc.save('ProjectBookPro_Marketing_Training_Manual.pdf');
        setDone(true);
      } catch (err) {
        console.error('PDF generation error:', err);
        alert('Error generating PDF. Please try again.');
      } finally {
        setGenerating(false);
      }
    }, 100);
  };

  return (
    <div className="space-y-5" data-testid="marketing-manual-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-heading)' }}>Marketing & Training Manual</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Generate a professional PDF manual with all ProjectBook Pro&trade; features, FAQ, and SGK Apps branding</p>
      </div>

      {/* Preview */}
      <Card className="p-5" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg" style={{ color: 'var(--text-heading)' }}>PDF Manual Contents</h2>
          <Button onClick={generatePDF} disabled={generating} data-testid="generate-pdf-btn">
            {generating ? (
              <><SpinnerIcon className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : done ? (
              <><CheckCircle className="w-4 h-4 mr-2" /> Download Again</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Generate & Download PDF</>
            )}
          </Button>
        </div>

        {done && (
          <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(0,200,83,0.1)', color: '#00C853' }}>
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> PDF downloaded successfully! Check your downloads folder.
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Cover Page Preview */}
          <div className="rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #002FA7, #001a5e)' }}>
            <p className="text-white text-xs font-medium opacity-70 mb-1">COVER PAGE</p>
            <p className="text-white text-xl font-bold">ProjectBook Pro&trade;</p>
            <p className="text-white text-sm opacity-80">Project ERP & Control System — Product & Training Manual</p>
            <p className="text-white text-xs opacity-60 mt-2">SGK Apps | info@sgkapps.com | VRV Technical & Technology Solutions FZ LLC</p>
          </div>

          {/* Section list */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FEATURE_SECTIONS.map((sec, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
                <div className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: `rgb(${sec.color.join(',')})` }}>
                  {i + 1}
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>{sec.title}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{sec.features.length} features documented</p>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 p-2.5 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
              <div className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold bg-[#FFAB00]">?</div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>FAQ</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>10 common questions answered</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2.5 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
              <div className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold bg-[#002FA7]">@</div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>Support & Contact</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>SGK Apps branding & contact info</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            Total: {FEATURE_SECTIONS.reduce((a, s) => a + s.features.length, 0)} features across {FEATURE_SECTIONS.length} modules
          </p>
        </div>
      </Card>
    </div>
  );
}
